import React, { useCallback, useEffect, useState } from "react";
import { ResponsivePie } from "@nivo/pie";
import { Grid2 } from "@mui/material";
import {
  AssetOrLapsesChartData,
  CountChartData,
} from "../../../../Models/Models";
import { GetLapsesCount } from "../../../../ApiServices/ApiRequests";
import {
  CtmLiveOverViewType,
  useCtmLiveOverView,
} from "../../../../ContextAPI/CtmLiveOverViewContext";
import { useTranslation } from "react-i18next";
import Loading from "../../../../Controller/LoadingController/Loading";
import ChartWebSocket from "../../../../WebSockets/ChartWebSocket";

// Define the shape of the data used in the chart

interface LapseData {
  id: string;
  label: string;
  value: number;
}

export const LapsesChart = () => {
  const { ctmLiveOverViewState, dispatchCtmLiveOverView } =
    useCtmLiveOverView();
  const { t } = useTranslation();

  const handleInputChange = useCallback(
    (
      field: keyof CtmLiveOverViewType,
      value: string | boolean | number | null | AssetOrLapsesChartData[]
    ) => {
      dispatchCtmLiveOverView({ type: "SET_FIELD", field, value });
    },
    [dispatchCtmLiveOverView]
  );

  const handleBulkChange = useCallback(
    (value: Partial<CtmLiveOverViewType>) => {
      dispatchCtmLiveOverView({ type: "BULK_UPDATE", value });
    },
    [dispatchCtmLiveOverView]
  );
  const [loading, setLoading] = useState<boolean>(false);

  // Async function to retrieve data
  const retrieveData = async (): Promise<void> => {
    try {
      setLoading(true);
      const result: CountChartData[] = await GetLapsesCount();
      // Assuming the data is in result.data.data
      const formattedData: AssetOrLapsesChartData[] = result?.map((ac) => ({
        id: ac.name,
        label: ac.name,
        value: ac.value,
      }));

      const uniqueArray: AssetOrLapsesChartData[] = Array.from(
        new Map(formattedData?.map((item) => [item.id, item])).values()
      );
      handleInputChange("LapsesCountChartData", uniqueArray);
    } catch (e) {
      console.error("Error fetching  Lapses count data:", e);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    retrieveData();
  }, []);

  const chartInfo: AssetOrLapsesChartData[] =
    ctmLiveOverViewState.LapsesCountChartData.length > 0
      ? ctmLiveOverViewState.LapsesCountChartData?.sort(
          (a, b) => b.value - a.value
        ).slice(0, 5)
      : [];

  if (chartInfo.length > 0) {
    return (
      <Grid2 style={{ height: 80 }}>
        {loading ? (
          <Loading loading={loading} type={`dots`} backDrop={false} />
        ) : (
          <ResponsivePie
            data={chartInfo}
            margin={{ right: 0, bottom: 5, left: 160, top: 3 }}
            innerRadius={0.7}
            padAngle={0.8}
            cornerRadius={3}
            colors={{ scheme: "brown_blueGreen" }}
            borderWidth={1}
            borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
            enableArcLabels={false}
            enableArcLinkLabels={false}
            theme={{
              text: {
                color: "black",
              },
              tooltip: {
                container: {
                  color: "black",
                  background: "white",
                },
              },
            }}
            defs={[
              {
                id: "dots",
                type: "patternDots",
                background: "inherit",
                color: "rgba(255, 255, 255, 0.3)",
                size: 4,
                padding: 1,
                stagger: true,
              },
              {
                id: "lines",
                type: "patternLines",
                background: "inherit",
                color: "rgba(255, 255, 255, 0.3)",
                rotation: -45,
                lineWidth: 6,
                spacing: 10,
              },
            ]}
            fill={[
              {
                match: {
                  id: "ruby",
                },
                id: "dots",
              },
              {
                match: {
                  id: "c",
                },
                id: "dots",
              },
              {
                match: {
                  id: "go",
                },
                id: "dots",
              },
              {
                match: {
                  id: "python",
                },
                id: "dots",
              },
              {
                match: {
                  id: "scala",
                },
                id: "lines",
              },
              {
                match: {
                  id: "lisp",
                },
                id: "lines",
              },
              {
                match: {
                  id: "elixir",
                },
                id: "lines",
              },
              {
                match: {
                  id: "javascript",
                },
                id: "lines",
              },
            ]}
            legends={[
              {
                anchor: "bottom-left",
                direction: "column",
                justify: false,
                translateX: -160,
                translateY: 0,
                itemWidth: 10,
                itemHeight: 5,
                itemsSpacing: 10,
                symbolSize: 12,
                itemDirection: "left-to-right",

                effects: [
                  {
                    on: "hover",
                    style: {
                      itemTextColor: "#000",
                    },
                  },
                ],
                //data: chartData.sort((a, b) => b.value - a.value).slice(0, 5),
              },
            ]}
          />
        )}
        <ChartWebSocket />
      </Grid2>
    );
  } else {
    return null;
  }
};
